/**
 * GeofenceSidebar — searchable all-company_sites list with verification badges.
 *
 * Phase 9 / Plan 09-06 — sourced from GET /v1/sites (useSites) so EVERY company_site
 * is listed, including sites with no jobs yet (Blocker 2 / DISPATCH-04).
 *
 * Verification badge: geofenceExists=true → "Verified" (success green);
 *                     geofenceExists=false → "Unverified" (warning amber).
 * DISABLED (tertiary) reserved for inactive sites — not surfaced in Phase 9 scope.
 *
 * Spec: 09-06-PLAN.md Task 2; 09-UI-SPEC.md §6 (sidebar 320px); Copywriting contract.
 */

import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import type { SiteSummary } from '@lodex-pro/shared';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { formatNumber } from '@lodex-pro/shared/number-format';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GeofenceSidebarProps {
  /** Full site list from GET /v1/sites (all company_sites, incl. jobless). */
  readonly sites: SiteSummary[];
  readonly selectedSiteId: string | null;
  readonly onSelect: (siteId: string) => void;
}

// ---------------------------------------------------------------------------
// Status filter (DEC-109) — All | Draft | Verified | Unverified
// ---------------------------------------------------------------------------

type StatusFilter = 'all' | 'draft' | 'verified' | 'unverified';
type SiteStatus = 'verified' | 'draft' | 'unverified';

const STATUS_STORAGE_KEY = 'geofence-status-filter';
const STATUS_TABS: ReadonlyArray<{ readonly key: StatusFilter; readonly label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'verified', label: 'Verified' },
  { key: 'unverified', label: 'Unverified' },
];

/** Three-state status, mirroring the VerificationBadge logic. */
function siteStatus(s: SiteSummary): SiteStatus {
  if (s.geofenceExists && s.geofenceVerified === true) return 'verified';
  if (s.geofenceExists) return 'draft';
  return 'unverified';
}

/** Read the persisted filter (sessionStorage) so it survives leaving + returning. */
function readPersistedStatus(): StatusFilter {
  try {
    const saved = sessionStorage.getItem(STATUS_STORAGE_KEY);
    if (saved === 'draft' || saved === 'verified' || saved === 'unverified' || saved === 'all') {
      return saved;
    }
  } catch {
    // sessionStorage unavailable (privacy mode / SSR) — fall through to default.
  }
  return 'all';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GeofenceSidebar({
  sites,
  selectedSiteId,
  onSelect,
}: GeofenceSidebarProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  // DEC-109 — verification-status filter, persisted for the session.
  const [status, setStatus] = useState<StatusFilter>(readPersistedStatus);
  React.useEffect(() => {
    try {
      sessionStorage.setItem(STATUS_STORAGE_KEY, status);
    } catch {
      // ignore — persistence is best-effort.
    }
  }, [status]);

  // Search filter first; status counts + the displayed list both derive from it
  // so the search box and the status tabs apply together (DEC-109).
  const searchFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter((s) => s.siteName.toLowerCase().includes(q));
  }, [sites, query]);

  const counts = useMemo(() => {
    const c = { all: searchFiltered.length, verified: 0, draft: 0, unverified: 0 };
    for (const s of searchFiltered) c[siteStatus(s)] += 1;
    return c;
  }, [searchFiltered]);

  const filteredSites = useMemo(
    () =>
      status === 'all' ? searchFiltered : searchFiltered.filter((s) => siteStatus(s) === status),
    [searchFiltered, status],
  );

  // Phase 8 / Task 8.2 — bulk verification progress (over the full site list).
  const verifiedCount = useMemo(
    () => sites.filter((s) => s.geofenceVerified === true).length,
    [sites],
  );

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  return (
    <div
      style={{
        width: '320px',
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-bg-secondary)',
        borderRight: '1px solid var(--color-border-primary)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--color-border-primary)',
        }}
      >
        <p
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
            marginBottom: '12px',
          }}
        >
          Sites
        </p>
        {/* Phase 8 / Task 8.2 — bulk count. */}
        <p
          data-testid="geofence-verified-count"
          style={{
            fontSize: '12px',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
            marginBottom: '12px',
          }}
        >
          {formatNumber(verifiedCount)} of {sites.length} sites verified
        </p>
        <Input
          placeholder="Search sites..."
          value={query}
          onChange={handleQueryChange}
          aria-label="Search sites"
          style={{ fontSize: '14px' }}
        />

        {/* DEC-109 — verification-status filter tabs (with counts). */}
        <div
          role="tablist"
          aria-label="Filter sites by status"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '12px' }}
        >
          {STATUS_TABS.map((t) => {
            const active = status === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={(): void => setStatus(t.key)}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-pill)',
                  border: '1px solid var(--color-border-primary)',
                  backgroundColor: active ? 'var(--color-accent-blue)' : 'transparent',
                  color: active ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
                  fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
                }}
              >
                {t.label} ({counts[t.key]})
              </button>
            );
          })}
        </div>
      </div>

      {/* Site list — grouped by type (Phase 24 / Task 24.6 adds Tank Batteries). */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filteredSites.length === 0 ? (
          <EmptyState filtered={query.trim().length > 0 || status !== 'all'} />
        ) : (
          <>
            <SiteGroup
              label="Other Sites"
              sites={filteredSites.filter(
                (s) => s.siteType !== 'DISPOSAL' && s.siteType !== 'TANK_BATTERY',
              )}
              selectedSiteId={selectedSiteId}
              onSelect={onSelect}
            />
            <SiteGroup
              label="Tank Batteries"
              sites={filteredSites.filter((s) => s.siteType === 'TANK_BATTERY')}
              selectedSiteId={selectedSiteId}
              onSelect={onSelect}
            />
            <SiteGroup
              label="Disposal Facilities"
              sites={filteredSites.filter((s) => s.siteType === 'DISPOSAL')}
              selectedSiteId={selectedSiteId}
              onSelect={onSelect}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Site group (section header + rows) — Phase 24 / Task 24.6
// ---------------------------------------------------------------------------

function SiteGroup({
  label,
  sites,
  selectedSiteId,
  onSelect,
}: {
  readonly label: string;
  readonly sites: readonly SiteSummary[];
  readonly selectedSiteId: string | null;
  readonly onSelect: (siteId: string) => void;
}): React.JSX.Element | null {
  if (sites.length === 0) return null;
  return (
    <div>
      <div
        style={{
          padding: '8px 16px 4px',
          fontSize: '11px',
          fontWeight: 700,
          color: 'var(--color-text-tertiary)',
        }}
      >
        {label}
      </div>
      {sites.map((site) => (
        <SiteRow
          key={site.siteId}
          site={site}
          selected={site.siteId === selectedSiteId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ filtered }: { readonly filtered: boolean }): React.JSX.Element {
  if (filtered) {
    return (
      <div
        style={{
          padding: '32px 16px',
          textAlign: 'center',
          color: 'var(--color-text-tertiary)',
          fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
        }}
      >
        <p style={{ fontSize: '14px' }}>No sites match your filters.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '32px 16px',
        textAlign: 'center',
        fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
      }}
    >
      {/* UI-SPEC exact copy: "No sites yet" / "Sites are added by administrators." */}
      <p
        style={{
          fontSize: '16px',
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          marginBottom: '8px',
        }}
      >
        No sites yet
      </p>
      <p
        style={{
          fontSize: '14px',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Sites are added by administrators.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Site row
// ---------------------------------------------------------------------------

interface SiteRowProps {
  readonly site: SiteSummary;
  readonly selected: boolean;
  readonly onSelect: (siteId: string) => void;
}

function SiteRow({ site, selected, onSelect }: SiteRowProps): React.JSX.Element {
  const handleClick = useCallback(() => {
    onSelect(site.siteId);
  }, [site.siteId, onSelect]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick();
      }}
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--color-border-primary)',
        backgroundColor: selected ? 'var(--color-bg-tertiary)' : 'transparent',
        borderLeft: selected ? '3px solid var(--color-accent-blue)' : '3px solid transparent',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      {/* Site name + badge row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <span
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: 0,
          }}
        >
          {/* Phase 8 / Task 8.1 — distinguish disposal facilities from pickup sites. */}
          {site.siteType === 'DISPOSAL' && (
            <span
              data-testid="disposal-tag"
              style={{
                flexShrink: 0,
                fontSize: '9px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                color: 'var(--color-accent-blue)',
                border: '1px solid var(--color-accent-blue)',
                borderRadius: '3px',
                padding: '1px 4px',
              }}
            >
              SWD
            </span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {site.siteName}
          </span>
        </span>
        <VerificationBadge
          geofenceExists={site.geofenceExists}
          geofenceVerified={site.geofenceVerified === true}
        />
      </div>

      {/* Operator name (secondary) */}
      <span
        style={{
          fontSize: '12px',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {site.operatorName}
      </span>

      {/* Add Geofence button (accent-blue secondary CTA) */}
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(site.siteId);
        }}
        style={{
          alignSelf: 'flex-start',
          marginTop: '4px',
          borderColor: 'var(--color-accent-blue)',
          color: 'var(--color-accent-blue)',
          fontSize: '12px',
        }}
      >
        Add Geofence
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verification badge
// ---------------------------------------------------------------------------

interface VerificationBadgeProps {
  readonly geofenceExists: boolean;
  readonly geofenceVerified: boolean;
}

/**
 * Phase 8 / Task 8.2 (DEC-080) — three-state badge:
 *   Verified (green)   — active geofence + is_verified
 *   Draft (amber)      — active geofence, not yet verified
 *   Unverified (grey)  — no active geofence
 */
function VerificationBadge({
  geofenceExists,
  geofenceVerified,
}: VerificationBadgeProps): React.JSX.Element {
  const base: React.CSSProperties = {
    color: 'var(--color-on-accent)',
    border: 'none',
    fontSize: '11px',
    flexShrink: 0,
  };
  if (geofenceExists && geofenceVerified) {
    return <Badge style={{ ...base, backgroundColor: 'var(--color-success)' }}>Verified</Badge>;
  }
  if (geofenceExists) {
    return <Badge style={{ ...base, backgroundColor: 'var(--color-warning)' }}>Draft</Badge>;
  }
  return (
    <Badge style={{ ...base, backgroundColor: 'var(--color-text-tertiary)' }}>Unverified</Badge>
  );
}
